import { clientStorage } from "@/lib/clientStorage";
import CloudDraftsPanel from "@/components/CloudDraftsPanel";
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useCloudDrafts } from "@/hooks/useCloudDrafts";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import { 
  FileText, 
  Upload, 
  Trash2, 
  Download,
  Calendar,
  Loader2,
  DollarSign,
  Plus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const YEARS = ["2025", "2026", "2027", "2028"];

const BRAND_DETAILS = {
  rga: {
    orgName: "Robyn Greensill & Associates",
    appPrefix: "RGA",
    defaultEmail: "rga@completelawsupport.com.au",
    textColor: "text-teal-700",
    textDarkColor: "text-teal-700 dark:text-teal-400",
    bgHeader: "bg-gradient-to-r from-teal-800 to-teal-700",
    bgButton: "bg-teal-700 hover:bg-teal-800 focus:ring-teal-500",
    borderColor: "border-teal-100",
    accentColor: "#0f766e",
    bgDragActive: "border-teal-500 bg-teal-50/50",
    borderDragInactive: "border-slate-200 hover:border-teal-400"
  },
  lacw: {
    orgName: "Law and Advocacy Centre for Women",
    appPrefix: "LACW",
    defaultEmail: "lacw@completelawsupport.com.au",
    textColor: "text-teal-700",
    textDarkColor: "text-teal-700 dark:text-teal-400",
    bgHeader: "bg-gradient-to-r from-teal-800 to-teal-700",
    bgButton: "bg-teal-700 hover:bg-teal-800 focus:ring-teal-500",
    borderColor: "border-teal-100",
    accentColor: "#0f766e",
    bgDragActive: "border-teal-500 bg-teal-50/50",
    borderDragInactive: "border-slate-200 hover:border-teal-400"
  },
  eas: {
    orgName: "EAS Legal",
    appPrefix: "EAS",
    defaultEmail: "eas@completelawsupport.com.au",
    textColor: "text-amber-800",
    textDarkColor: "text-amber-800 dark:text-amber-400",
    bgHeader: "bg-gradient-to-r from-slate-900 to-slate-800",
    bgButton: "bg-amber-700 hover:bg-amber-800 focus:ring-amber-500",
    borderColor: "border-amber-100",
    accentColor: "#C29A5B",
    bgDragActive: "border-amber-500 bg-amber-50/50",
    borderDragInactive: "border-slate-200 hover:border-amber-400"
  },
  vals: {
    orgName: "Victorian Aboriginal Legal Service",
    appPrefix: "VALS",
    defaultEmail: "vals@completelawsupport.com.au",
    textColor: "text-rose-700",
    textDarkColor: "text-rose-700 dark:text-rose-400",
    bgHeader: "bg-gradient-to-r from-rose-800 to-rose-700",
    bgButton: "bg-rose-700 hover:bg-rose-800 focus:ring-rose-500",
    borderColor: "border-rose-100",
    accentColor: "#be123c",
    bgDragActive: "border-rose-500 bg-rose-50/50",
    borderDragInactive: "border-slate-200 hover:border-rose-400"
  }
};

export default function MonthlyTrustReceipts({ isPortalMode = false, brandType = "rga", isRgaPortal = false }) {
  const { user } = useAuth();
  const { drafts, fetchDrafts, saveDraft, deleteDraft, isLoading } = useCloudDrafts();
  const { fetchDrafts: fetchSubmissionDrafts } = useCloudDrafts();
  const brandObj = BRAND_DETAILS[brandType] || BRAND_DETAILS.rga;

  const formatCurrency = (val) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(val);

  const [activeReport, setActiveReport] = useState(null); // { type: 'trust' | 'reconciliation' | 'banking', month: string, year: string }

  const mockReportData = useMemo(() => {
    if (!activeReport) return null;
    const { type, month, year } = activeReport;
    
    const seed = month.length + Number(year);
    const count = 5 + (seed % 6);
    const transactions = [];
    let totalAmt = 0;
    
    const descriptions = {
      trust: [
        "VLA Legal Aid Funding Advance",
        "Client Retainer Deposit - Matter #8401",
        "Cost Agreement Settlement - Smith & Co",
        "VLA Disbursals Reimbursement",
        "Client Trust Allocation - Criminal Appeal",
        "VLA Grant Clearance - County Court Hearing"
      ],
      reconciliation: [
        "Bank Interest Earned (Trust Account)",
        "Trust Account Admin Fee Debit",
        "VLA Direct Credit Reconciliation",
        "Unallocated Deposit Matching",
        "Cleared Bank Draft - Cheque #9204",
        "EFT Transfer Clearing Check"
      ],
      banking: [
        "Trust Account Transfer to General - Invoice #293",
        "VLA EFT Disbursements Remittance",
        "Bank EFT Inward Transfer - Retainer",
        "VLA Funding Disbursement Payment",
        "Counsel Fee EFT Remittance - Clerk G",
        "Office Cost Transfer clearance"
      ]
    }[type] || ["General Allocation"];

    for (let i = 0; i < count; i++) {
      const amt = 1000 + ((seed * (i + 1) * 31) % 8000) + ((seed % 10) * 100);
      const isDebit = type === "banking" || (type === "reconciliation" && i % 3 === 0);
      totalAmt += isDebit ? -amt : amt;
      
      transactions.push({
        date: `${i + 1} ${month.slice(0, 3)} ${year}`,
        ref: `${type.toUpperCase()}-REF-${10000 + (seed * (i + 2)) % 90000}`,
        description: descriptions[i % descriptions.length],
        amount: isDebit ? -amt : amt
      });
    }

    return {
      month,
      year,
      type,
      totalAmount: totalAmt,
      transactions,
      accountNo: "RGA-TRUST-88402-991",
      status: type === "reconciliation" ? "Reconciled" : "Completed"
    };
  }, [activeReport]);

  // Form states
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  
  // New categories and manual date states
  const [uploadCategory, setUploadCategory] = useState("Receipt");
  const [customCategoryName, setCustomCategoryName] = useState("");
  const [manualDate, setManualDate] = useState(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  // File upload state
  const [attachedFile, setAttachedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Additional RGA specific upload states
  const [reconciliationFile, setReconciliationFile] = useState(null);
  const [isDraggingReconcile, setIsDraggingReconcile] = useState(false);

  const [bankingFile, setBankingFile] = useState(null);
  const [isDraggingBanking, setIsDraggingBanking] = useState(false);

  // Individual trust deposit submission states
  const [clients, setClients] = useState([]);
  const [clientType, setClientType] = useState("existing"); // "existing" | "new"
  const [selectedClientId, setSelectedClientId] = useState("");
  const [newClientName, setNewClientName] = useState("");
  const [newClientAddress, setNewClientAddress] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");
  const [newClientMatterRef, setNewClientMatterRef] = useState("");
  const [newClientDescription, setNewClientDescription] = useState("");
  const [depositNotes, setDepositNotes] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Bank Receipt"); // Bank Receipt | Invoice | Backsheet | Trust Agreement | Other
  const [otherCategoryName, setOtherCategoryName] = useState("");
  const [stagedFiles, setStagedFiles] = useState([]); // Array of { file, category, id }
  const [submittingDeposit, setSubmittingDeposit] = useState(false);

  // For Admin to display submitted trust deposits
  const [submissions, setSubmissions] = useState([]);

  // Fetch submissions from cloud drafts
  const fetchSubmissions = async () => {
    try {
      const allDrafts = await fetchSubmissionDrafts("rga_trust_submission", "rga");
      if (allDrafts) {
        setSubmissions(allDrafts);
      }
    } catch (err) {
      console.error("Failed to load submissions:", err);
    }
  };

  useEffect(() => {
    // If brandType is rga, fetch submissions as well
    if (brandType === "rga") {
      fetchSubmissions();
    }
  }, [brandType]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const stored = clientStorage.getItem("rga_trust_clients");
      if (stored) {
        setClients(JSON.parse(stored));
      } else {
        const initial = [
          { id: "demo-client", name: "Example Client (Fictional)", address: "", phone: "", matterRef: "DEMO-0001", description: "Fictional demonstration record" },
          { id: "c2", name: "Sarah Connor", address: "88 Elizabeth St, Richmond VIC 3121", phone: "0423 456 789", matterRef: "RGA-2026-1182", description: "Family Law - Custody dispute" },
          { id: "c3", name: "Bruce Wayne", address: "100 Exhibition St, Melbourne VIC 3000", phone: "0499 999 999", matterRef: "RGA-2026-0072", description: "Commercial Law - Property Acquisition" }
        ];
        clientStorage.setItem("rga_trust_clients", JSON.stringify(initial));
        setClients(initial);
      }
    }
  }, []);



  const handleSaveClient = () => {
    if (!newClientName.trim() || !newClientMatterRef.trim()) {
      toast.error("Client Name and Matter Reference are required.");
      return null;
    }
    const newClient = {
      id: `client_${Date.now()}`,
      name: newClientName,
      address: newClientAddress,
      phone: newClientPhone,
      matterRef: newClientMatterRef,
      description: newClientDescription
    };
    const updated = [...clients, newClient];
    clientStorage.setItem("rga_trust_clients", JSON.stringify(updated));
    setClients(updated);
    
    // Auto-select the newly created client
    setSelectedClientId(newClient.id);
    setClientType("existing");
    
    // Clear new client form
    setNewClientName("");
    setNewClientAddress("");
    setNewClientPhone("");
    setNewClientMatterRef("");
    setNewClientDescription("");
    toast.success(`Client ${newClient.name} successfully saved to client database!`);
    return newClient;
  };

  const handleStageFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const categoryName = selectedCategory === "Other" ? otherCategoryName : selectedCategory;
    if (selectedCategory === "Other" && !otherCategoryName.trim()) {
      toast.error("Please enter a custom category name.");
      return;
    }

    const newStaged = files.map(file => ({
      id: `staged_${Date.now()}_${Math.random()}`,
      file,
      category: categoryName
    }));

    setStagedFiles(prev => [...prev, ...newStaged]);
    setOtherCategoryName("");
  };

  const handleRemoveStagedFile = (id) => {
    setStagedFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleSubmitTrustDeposit = async (e) => {
    e.preventDefault();
    if (submittingDeposit) return;

    let activeClient = null;
    if (clientType === "new") {
      activeClient = handleSaveClient();
      if (!activeClient) return; // Error message was already shown
    } else {
      activeClient = clients.find(c => c.id === selectedClientId);
    }

    if (!activeClient) {
      toast.error("Please select a client or save a new one first.");
      return;
    }

    if (stagedFiles.length === 0) {
      toast.error("Please attach at least one file before submitting.");
      return;
    }

    setSubmittingDeposit(true);
    try {
      // Upload all staged files to base44 storage
      const uploadedFiles = [];
      for (const staged of stagedFiles) {
        const { file_url } = await base44.integrations.Core.UploadFile({ file: staged.file });
        uploadedFiles.push({
          name: staged.file.name,
          size: staged.file.size,
          category: staged.category,
          url: file_url
        });
      }

      const submissionId = `rga_trust_sub_${Date.now()}`;
      await saveDraft({
        id: submissionId,
        formType: "rga_trust_submission",
        draftName: `Trust Deposit: ${activeClient.name} (${activeClient.matterRef})`,
        savedBy: user?.full_name || user?.name || "RGA Portal User",
        savedByEmail: user?.email || brandObj.defaultEmail,
        brand: "rga",
        formData: {
          clientName: activeClient.name,
          clientAddress: activeClient.address,
          clientPhone: activeClient.phone,
          matterRef: activeClient.matterRef,
          matterDescription: activeClient.description,
          notes: depositNotes,
          files: uploadedFiles,
          submittedAt: new Date().toISOString(),
          status: "pending_action"
        }
      });

      toast.success("Client trust deposit and documents submitted successfully!");
      
      // Reset form states
      setStagedFiles([]);
      setDepositNotes("");
      setSelectedClientId("");
      setClientType("existing");
      
      // Refresh submissions
      fetchSubmissions();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to submit client trust deposit");
    } finally {
      setSubmittingDeposit(false);
    }
  };

  const handleActionSubmission = async (draftId) => {
    if (!window.confirm("Are you sure you want to mark this trust deposit submission as actioned?")) return;
    try {
      await deleteDraft(draftId, "rga_trust_submission", "rga");
      toast.success("Submission marked as actioned.");
      fetchSubmissions();
    } catch (err) {
      console.error("Failed to delete draft:", err);
      toast.error("Failed to mark submission as actioned.");
    }
  };
 
  // Load receipts on mount
  useEffect(() => {
    fetchDrafts("monthly_trust_receipts", brandType);
  }, [fetchDrafts, brandType]);
 
  // Drag and drop handlers - Trust Receipts
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
 
  const handleDragLeave = () => {
    setIsDragging(false);
  };
 
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setAttachedFile(files[0]);
    }
  };
 
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setAttachedFile(files[0]);
    }
  };

  // Drag and drop handlers - Reconciliation Receipts
  const handleDragOverReconcile = (e) => {
    e.preventDefault();
    setIsDraggingReconcile(true);
  };

  const handleDragLeaveReconcile = () => {
    setIsDraggingReconcile(false);
  };

  const handleDropReconcile = (e) => {
    e.preventDefault();
    setIsDraggingReconcile(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setReconciliationFile(files[0]);
    }
  };

  // Drag and drop handlers - Banking/Transfer/Payment
  const handleDragOverBanking = (e) => {
    e.preventDefault();
    setIsDraggingBanking(true);
  };

  const handleDragLeaveBanking = () => {
    setIsDraggingBanking(false);
  };

  const handleDropBanking = (e) => {
    e.preventDefault();
    setIsDraggingBanking(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setBankingFile(files[0]);
    }
  };
 
  // Submit and Upload Receipt
  const handleUploadReceipt = async (e) => {
    e.preventDefault();
    if (isUploading) return;
    if (!attachedFile) return toast.error("Please select or drop a receipt file to upload");

    const selectedDate = new Date(manualDate);
    if (isNaN(selectedDate.getTime())) {
      return toast.error("Please enter a valid date");
    }
    const enteredMonth = MONTHS[selectedDate.getMonth()];
    const enteredYear = selectedDate.getFullYear().toString();

    const categoryName = uploadCategory === "other" ? customCategoryName.trim() : uploadCategory;
    if (uploadCategory === "other" && !categoryName) {
      return toast.error("Please enter a custom category name");
    }



    setIsUploading(true);
    try {
      // 1. Upload main trust receipt to base44 storage
      const { file_url } = await base44.integrations.Core.UploadFile({ file: attachedFile });

      const draftId = `trust_receipt_${enteredMonth}_${enteredYear}_${categoryName.replace(/\s+/g, '_')}_${Date.now()}`;
      const payload = {
        month: enteredMonth,
        year: enteredYear,
        date: manualDate,
        category: categoryName,
        fileName: attachedFile.name,
        fileSize: attachedFile.size,
        fileUrl: file_url,
        uploadedAt: new Date().toISOString()
      };

      // 2. Save as cloud draft
      await saveDraft({
        id: draftId,
        formType: "monthly_trust_receipts",
        draftName: `Trust Document: ${categoryName} (${enteredMonth} ${enteredYear})`,
        savedBy: user?.full_name || user?.name || `${brandObj.appPrefix} Operator`,
        savedByEmail: user?.email || brandObj.defaultEmail,
        brand: brandType,
        formData: payload,
        matter_id: null
      });

      toast.success(`Successfully uploaded ${categoryName} for ${enteredMonth} ${enteredYear}!`);
      setAttachedFile(null);
      setCustomCategoryName("");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to upload trust receipt");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete Receipt
  const handleDeleteReceipt = async (draftId) => {
    if (!window.confirm("Are you sure you want to delete this trust receipt record?")) return;
    try {
      await deleteDraft(draftId, "monthly_trust_receipts", brandType);
      toast.success("Receipt record deleted successfully.");
    } catch (err) {
      toast.error(err.message || "Failed to delete record");
    }
  };

  // Helper to download files programmatically
  const handleDownloadFile = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error("No file URL available for this document.");
      return;
    }
    
    try {
      if (fileUrl.startsWith("data:")) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = fileName || "download.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Downloaded: ${fileName}`);
      } else {
        window.open(fileUrl, '_blank');
      }
    } catch (err) {
      console.error("Failed to download file:", err);
      toast.error("Failed to download file.");
    }
  };

  // Group drafts by Year
  const groupedReceipts = useMemo(() => {
    const groups = {};
    drafts.forEach(d => {
      if (!d.form_data) return;
      const yr = d.form_data.year;
      if (!groups[yr]) groups[yr] = [];
      groups[yr].push(d);
    });
    // Sort months within year
    Object.keys(groups).forEach(yr => {
      groups[yr].sort((a, b) => {
        const aMonthIdx = MONTHS.indexOf(a.form_data.month);
        const bMonthIdx = MONTHS.indexOf(b.form_data.month);
        return bMonthIdx - aMonthIdx; // Descending order of months
      });
    });
    return groups;
  }, [drafts]);

  const yearsList = useMemo(() => {
    return Object.keys(groupedReceipts).sort((a, b) => Number(b) - Number(a)); // Descending order of years
  }, [groupedReceipts]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Staged files are File objects and cannot go into a draft, so a restored draft
            carries the client and deposit details but not the attachments. */}
        <div className="flex justify-end print:hidden">
          <CloudDraftsPanel
            formType="monthly_trust_receipts"
            formData={{
              selectedMonth, selectedYear, clientType, selectedClientId,
              newClientName, newClientAddress, newClientPhone,
              newClientMatterRef, newClientDescription,
              depositNotes, selectedCategory, otherCategoryName,
            }}
            onLoadDraft={(draft) => {
              if (!draft) return;
              if (draft.selectedMonth) setSelectedMonth(draft.selectedMonth);
              if (draft.selectedYear) setSelectedYear(draft.selectedYear);
              if (draft.clientType) setClientType(draft.clientType);
              setSelectedClientId(draft.selectedClientId ?? "");
              setNewClientName(draft.newClientName ?? "");
              setNewClientAddress(draft.newClientAddress ?? "");
              setNewClientPhone(draft.newClientPhone ?? "");
              setNewClientMatterRef(draft.newClientMatterRef ?? "");
              setNewClientDescription(draft.newClientDescription ?? "");
              setDepositNotes(draft.depositNotes ?? "");
              if (draft.selectedCategory) setSelectedCategory(draft.selectedCategory);
              setOtherCategoryName(draft.otherCategoryName ?? "");
            }}
            brand={brandType}
          />
        </div>
        
        {/* Title Header */}
        <div className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <DollarSign className={`w-7 h-7 ${brandObj.textColor}`} />
              Monthly Trust Receipts
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Upload and manage monthly bank statements and receipt records from the {brandObj.orgName} Trust Account.
            </p>
          </div>
        </div>

        {/* Console Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {isRgaPortal ? (
            <div className="lg:col-span-5">
              {/* Submit Client Trust Deposit Form */}
              <Card className="border border-slate-200 dark:border-slate-800 shadow-sm">
                <CardHeader className="bg-slate-900 text-white rounded-t-lg py-4">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Plus className="w-5 h-5 text-teal-400" />
                    Submit Client Trust Deposit
                  </CardTitle>
                  <CardDescription className="text-slate-300 text-xs">
                    File bank receipts, invoices, backsheets, and trust agreements.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-5">
                  <form onSubmit={handleSubmitTrustDeposit} className="space-y-4">
                    {/* Client Type Selector */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Client Type</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant={clientType === "existing" ? "default" : "outline"}
                          className="h-8 text-xs font-semibold"
                          onClick={() => setClientType("existing")}
                        >
                          Existing Client
                        </Button>
                        <Button
                          type="button"
                          variant={clientType === "new" ? "default" : "outline"}
                          className="h-8 text-xs font-semibold"
                          onClick={() => setClientType("new")}
                        >
                          New Client
                        </Button>
                      </div>
                    </div>

                    {/* Client fields based on Type */}
                    {clientType === "existing" ? (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Select Client</Label>
                        <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                          <SelectTrigger className="text-xs mt-1">
                            <SelectValue placeholder="Select client matter..." />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} ({c.matterRef})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {/* Selected Client details preview */}
                        {selectedClientId && (
                          (() => {
                            const c = clients.find(cl => cl.id === selectedClientId);
                            if (!c) return null;
                            return (
                              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-150 dark:border-slate-800 text-[10px] space-y-1 mt-2">
                                <div className="font-semibold text-slate-700 dark:text-slate-350">{c.name}</div>
                                <div><span className="font-medium text-slate-450">Matter Ref:</span> {c.matterRef}</div>
                                <div><span className="font-medium text-slate-450">Phone:</span> {c.phone || "N/A"}</div>
                                <div><span className="font-medium text-slate-450">Address:</span> {c.address || "N/A"}</div>
                                <div className="italic text-slate-550 mt-1">{c.description || "No description provided"}</div>
                              </div>
                            );
                          })()
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-900 border border-slate-150 dark:border-slate-800 rounded-lg">
                        <span className="text-[10px] font-bold text-teal-650 dark:text-teal-400 uppercase tracking-wider">New Client Registration</span>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold text-slate-700">Client Name *</Label>
                          <input
                            type="text"
                            placeholder="Full name"
                            value={newClientName}
                            onChange={e => setNewClientName(e.target.value)}
                            className="w-full text-xs p-2 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold text-slate-700">Address</Label>
                          <input
                            type="text"
                            placeholder="Street, Suburb, Postcode"
                            value={newClientAddress}
                            onChange={e => setNewClientAddress(e.target.value)}
                            className="w-full text-xs p-2 border border-slate-200 rounded bg-white"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold text-slate-700">Phone</Label>
                          <input
                            type="text"
                            placeholder="Mobile / Office number"
                            value={newClientPhone}
                            onChange={e => setNewClientPhone(e.target.value)}
                            className="w-full text-xs p-2 border border-slate-200 rounded bg-white"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold text-slate-700">Matter Reference Number *</Label>
                          <input
                            type="text"
                            placeholder="e.g. RGA-2026-0001"
                            value={newClientMatterRef}
                            onChange={e => setNewClientMatterRef(e.target.value)}
                            className="w-full text-xs p-2 border border-slate-200 rounded bg-white"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[10px] font-semibold text-slate-700">Description of Matter</Label>
                          <textarea
                            placeholder="Legal matter details..."
                            rows={2}
                            value={newClientDescription}
                            onChange={e => setNewClientDescription(e.target.value)}
                            className="w-full text-xs p-2 border border-slate-200 rounded resize-none bg-white"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full h-8 text-[11px] font-bold uppercase text-teal-850 hover:bg-teal-50 border-teal-200 bg-white"
                          onClick={handleSaveClient}
                        >
                          Save Client to Database
                        </Button>
                      </div>
                    )}

                    <hr className="border-slate-100 dark:border-slate-800" />

                    {/* File Attachment Category */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold text-slate-700">Document Type / Category</Label>
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="text-xs mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Bank Receipt">Bank Receipt</SelectItem>
                          <SelectItem value="Invoice">Invoice</SelectItem>
                          <SelectItem value="Backsheet">Backsheet</SelectItem>
                          <SelectItem value="Trust Agreement">Trust Agreement</SelectItem>
                          <SelectItem value="Other">Other (Custom Category)</SelectItem>
                        </SelectContent>
                      </Select>

                      {selectedCategory === "Other" && (
                        <div className="space-y-1 mt-1.5">
                          <Label className="text-[10px] font-semibold text-slate-500">Custom Category Name</Label>
                          <input
                            type="text"
                            placeholder="Enter manual category name"
                            value={otherCategoryName}
                            onChange={e => setOtherCategoryName(e.target.value)}
                            className="w-full text-xs p-2 border border-slate-200 rounded focus:outline-none focus:ring-1 bg-white"
                          />
                        </div>
                      )}
                    </div>

                    {/* Multiple File Picker Dropzone */}
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-slate-700">Attach Document(s)</Label>
                      <div
                        className="mt-1 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all hover:bg-slate-50 border-slate-200"
                        onClick={() => document.getElementById("deposit-files-input").click()}
                      >
                        <input
                          type="file"
                          id="deposit-files-input"
                          multiple
                          className="hidden"
                          onChange={handleStageFiles}
                          onClick={e => e.stopPropagation()}
                        />
                        <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-slate-650">Select files to upload</p>
                          <p className="text-[9px] text-slate-400">Multiple files supported (PDF/images)</p>
                        </div>
                      </div>
                    </div>

                    {/* Staged Files List */}
                    {stagedFiles.length > 0 && (
                      <div className="space-y-1.5">
                        <Label className="text-xs font-semibold text-slate-700">Staged Files ({stagedFiles.length})</Label>
                        <div className="max-h-[160px] overflow-y-auto space-y-1.5 border border-slate-150 rounded-lg p-2 bg-slate-50">
                          {stagedFiles.map(sf => (
                            <div key={sf.id} className="flex items-center justify-between gap-3 text-left w-full p-2 bg-white border border-slate-100 rounded text-[10px]">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="bg-slate-105 text-slate-800 font-bold px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wide">
                                    {sf.category}
                                  </span>
                                  <span className="font-semibold text-slate-700 truncate">{sf.file.name}</span>
                                </div>
                                <p className="text-[9px] text-slate-450 mt-0.5">{(sf.file.size / 1024).toFixed(1)} KB</p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="w-6 h-6 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded shrink-0"
                                onClick={() => handleRemoveStagedFile(sf.id)}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes field */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Notes / Comments</Label>
                      <textarea
                        placeholder="Provide any additional deposit instructions or notes..."
                        rows={2}
                        value={depositNotes}
                        onChange={e => setDepositNotes(e.target.value)}
                        className="w-full text-xs p-2 border border-slate-200 rounded resize-none focus:outline-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-slate-900 hover:bg-slate-850 text-white font-semibold text-xs py-2.5 rounded shadow-sm transition"
                      disabled={submittingDeposit}
                    >
                      {submittingDeposit ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Submitting Deposit Request...
                        </span>
                      ) : (
                        "Submit Trust Deposit"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="lg:col-span-5">
              <Card className={`${brandObj.borderColor} shadow-sm`}>
              <CardHeader className={`${brandObj.bgHeader} text-white rounded-t-lg py-4`}>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload Trust Document
                </CardTitle>
                <CardDescription className="text-slate-100 text-xs">
                  Select type, enter date, and attach document.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-5">
                <form onSubmit={handleUploadReceipt} className="space-y-4">
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Document Type / Category</Label>
                      <Select value={uploadCategory} onValueChange={setUploadCategory}>
                        <SelectTrigger className="text-xs mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Receipt">Receipt</SelectItem>
                          <SelectItem value="Banking">Banking</SelectItem>
                          <SelectItem value="Payments">Payments</SelectItem>
                          <SelectItem value="transfers">Transfers</SelectItem>
                          <SelectItem value="monthly reconcilitation">Monthly Reconciliation</SelectItem>
                          <SelectItem value="other">Other (Custom Name)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {uploadCategory === "other" && (
                      <div className="space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                        <Label className="text-xs font-semibold text-slate-700">Custom Category Name</Label>
                        <input
                          type="text"
                          value={customCategoryName}
                          onChange={(e) => setCustomCategoryName(e.target.value)}
                          placeholder="e.g. Audit Letter"
                          className="w-full text-xs mt-1 px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-500"
                          required
                        />
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-slate-700">Document Date</Label>
                      <input
                        type="date"
                        value={manualDate}
                        onChange={(e) => setManualDate(e.target.value)}
                        className="w-full text-xs mt-1 px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Dropzone */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Receipt File (PDF or Image)</Label>
                    <div 
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`mt-1.5 border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all ${
                        isDragging ? brandObj.bgDragActive : brandObj.borderDragInactive
                      }`}
                      onClick={() => document.getElementById("trust-file-upload").click()}
                    >
                      <input 
                        type="file" 
                        id="trust-file-upload" 
                        className="hidden" 
                        onChange={handleFileSelect}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                      {attachedFile ? (
                        <div className="flex items-center justify-between gap-3 text-left w-full">
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-semibold ${brandObj.textColor} truncate`}>{attachedFile.name}</p>
                            <p className="text-[10px] text-slate-400">{(attachedFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="w-7 h-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachedFile(null);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-slate-600 font-semibold">Drag & Drop Trust Receipt here</p>
                          <p className="text-[10px] text-slate-400">or click to browse local files (PDF/images)</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className={`w-full ${brandObj.bgButton} text-white font-semibold text-xs py-2.5 rounded shadow-sm transition`}
                    disabled={isUploading || isLoading}
                  >
                    {isUploading ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Uploading to Cloud...
                      </span>
                    ) : (
                      "Upload and File"
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Right Column: List of Receipts */}
           <div className={`${isRgaPortal ? "lg:col-span-12" : "lg:col-span-7"} space-y-6`}>
            {!isRgaPortal && brandType === "rga" && submissions.length > 0 && (
              <Card className="border border-amber-200 dark:border-amber-900 bg-amber-50/20 dark:bg-amber-950/20 shadow-sm">
                <CardHeader className="py-4 border-b border-amber-100 dark:border-amber-900">
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-slate-800 dark:text-slate-200">
                    <Calendar className="w-4.5 h-4.5 text-amber-550" />
                    Pending Client Trust Deposits ({submissions.length})
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Client deposits submitted via the RGA Portal awaiting actioning.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 space-y-3.5">
                  {submissions.map(sub => (
                    <div 
                      key={sub.id} 
                      className="p-4 bg-white dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-lg shadow-sm space-y-3 text-xs"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h4 className="font-bold text-slate-800 dark:text-slate-150 text-xs">
                            {sub.form_data?.clientName}
                          </h4>
                          <div className="text-[10px] text-slate-400 font-medium mt-0.5">
                            Matter Ref: {sub.form_data?.matterRef} • Submitted by: {sub.saved_by} ({new Date(sub.form_data?.submittedAt || sub.created_at).toLocaleString("en-AU")})
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-[10px] h-7 px-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200 font-bold uppercase tracking-wider"
                          onClick={() => handleActionSubmission(sub.id)}
                        >
                          Mark as Actioned
                        </Button>
                      </div>

                      {/* Client Details Preview */}
                      <div className="grid grid-cols-2 gap-3 p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-100 dark:border-slate-800 text-[10px]">
                        <div><span className="font-medium text-slate-400">Phone:</span> {sub.form_data?.clientPhone || "N/A"}</div>
                        <div><span className="font-medium text-slate-400">Address:</span> {sub.form_data?.clientAddress || "N/A"}</div>
                        <div className="col-span-2"><span className="font-medium text-slate-400">Matter Detail:</span> {sub.form_data?.matterDescription || "N/A"}</div>
                      </div>

                      {/* Attached Files list */}
                      {sub.form_data?.files && sub.form_data.files.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="text-[9px] font-bold text-slate-450 dark:text-slate-500 uppercase tracking-wider">Attached Documents ({sub.form_data.files.length})</div>
                          <div className="grid grid-cols-1 gap-1.5">
                            {sub.form_data.files.map((f, fIdx) => (
                              <div key={`sub-file-${fIdx}`} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 text-[10px]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span className="bg-slate-100 text-slate-800 font-bold px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wide">
                                    {f.category}
                                  </span>
                                  <span className="font-semibold text-slate-700 truncate">{f.name}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-teal-700 hover:text-teal-900 hover:bg-teal-50"
                                    onClick={() => setPreviewUrl(f.url)}
                                  >
                                    Preview
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                                    onClick={() => handleDownloadFile(f.url, f.name)}
                                  >
                                    <Download className="w-3 h-3 mr-1" /> Download
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {sub.form_data?.notes && (
                        <div className="p-2.5 bg-yellow-50/40 border border-yellow-100 dark:border-yellow-900 rounded text-[10px] text-slate-600 italic">
                          <span className="font-bold not-italic block mb-0.5 text-slate-700">Notes:</span>
                          {sub.form_data.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {yearsList.length === 0 ? (
              <Card className="border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 shadow-none border-dashed p-10 text-center flex flex-col items-center justify-center min-h-[300px]">
                <Calendar className="w-12 h-12 text-slate-400 mb-3" />
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">No Trust Receipts Filed</h3>
                <p className="text-xs text-slate-450 dark:text-slate-500 mt-1 max-w-[280px] leading-relaxed">
                  There are no trust receipt statement uploads filed in this account. Use the left panel to upload your first monthly receipt.
                </p>
              </Card>
            ) : (
              yearsList.map(year => (
                <div key={year} className="space-y-3">
                  <h2 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b pb-1">
                    Year: {year}
                  </h2>
                  <div className="grid gap-3">
                    {groupedReceipts[year].map(d => (
                      <div 
                        key={d.id} 
                        className="flex flex-col p-4 bg-white dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 gap-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100`}>
                              <FileText className={`w-5 h-5 ${brandObj.textColor}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200">
                                  {d.form_data.month} {d.form_data.year}
                                </h3>
                                {d.form_data.category && (
                                  <span className="bg-teal-50 dark:bg-teal-950/30 text-teal-700 dark:text-teal-400 border border-teal-100 dark:border-teal-900 font-bold px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wide">
                                    {d.form_data.category}
                                  </span>
                                )}
                              </div>
                              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 truncate max-w-[240px]">
                                {d.form_data.fileName} • {(d.form_data.fileSize / 1024).toFixed(1)} KB
                              </p>
                              {d.form_data.date && (
                                <p className="text-[9px] text-slate-450 dark:text-slate-500 mt-0.5">
                                  Document Date: {new Date(d.form_data.date).toLocaleDateString("en-AU", { day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                              <p className="text-[9px] text-slate-400 mt-0.5">
                                Uploaded by: {d.saved_by} ({new Date(d.form_data.uploadedAt || d.updated_at).toLocaleDateString("en-AU")})
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs px-2.5 bg-slate-50 hover:bg-slate-100 text-slate-800 border-slate-200"
                              onClick={() => setPreviewUrl(d.form_data.fileUrl)}
                            >
                              Preview
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-xs px-2.5"
                              onClick={() => handleDownloadFile(d.form_data.fileUrl, d.form_data.fileName)}
                            >
                              <Download className="w-3.5 h-3.5 mr-1" />
                              Download
                            </Button>
                            {brandType === "rga" && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-xs px-2.5 bg-teal-50 hover:bg-teal-100 text-teal-800 border-teal-200 font-semibold"
                                onClick={() => setActiveReport({ type: 'trust', month: d.form_data.month, year: d.form_data.year })}
                              >
                                Run Report
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="w-8 h-8 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded"
                              onClick={() => handleDeleteReceipt(d.id)}
                              disabled={isLoading}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Extra files rendered inside RGA brand if they exist */}
                        {(d.form_data.reconciliationFileUrl || d.form_data.bankingFileUrl) && (
                          <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 mt-1 space-y-2">
                            {d.form_data.reconciliationFileUrl && (
                              <div className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 text-[11px]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 shrink-0" />
                                  <span className="font-semibold text-slate-700 dark:text-slate-350 truncate">
                                    Reconciliation: {d.form_data.reconciliationFileName || "reconciliation-receipt.pdf"}
                                  </span>
                                  {d.form_data.reconciliationFileSize && (
                                    <span className="text-slate-400 dark:text-slate-500">({(d.form_data.reconciliationFileSize / 1024).toFixed(1)} KB)</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-teal-700 hover:text-teal-900 hover:bg-teal-50"
                                    onClick={() => setPreviewUrl(d.form_data.reconciliationFileUrl)}
                                  >
                                    Preview
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-teal-750 hover:text-teal-900 hover:bg-teal-50"
                                    onClick={() => handleDownloadFile(d.form_data.reconciliationFileUrl, d.form_data.reconciliationFileName)}
                                  >
                                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-teal-800 hover:text-teal-950 hover:bg-teal-50/80 font-semibold"
                                    onClick={() => setActiveReport({ type: 'reconciliation', month: d.form_data.month, year: d.form_data.year })}
                                  >
                                    Run Report
                                  </Button>
                                </div>
                              </div>
                            )}

                            {d.form_data.bankingFileUrl && (
                              <div className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 text-[11px]">
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                  <span className="font-semibold text-slate-700 dark:text-slate-350 truncate">
                                    Banking/Payment: {d.form_data.bankingFileName || "banking-record.pdf"}
                                  </span>
                                  {d.form_data.bankingFileSize && (
                                    <span className="text-slate-400 dark:text-slate-500">({(d.form_data.bankingFileSize / 1024).toFixed(1)} KB)</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
                                    onClick={() => setPreviewUrl(d.form_data.bankingFileUrl)}
                                  >
                                    Preview
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-emerald-750 hover:text-emerald-900 hover:bg-emerald-50"
                                    onClick={() => handleDownloadFile(d.form_data.bankingFileUrl, d.form_data.bankingFileName)}
                                  >
                                    <Download className="w-3.5 h-3.5 mr-1" /> Download
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-emerald-800 hover:text-emerald-950 hover:bg-emerald-50/80 font-semibold"
                                    onClick={() => setActiveReport({ type: 'banking', month: d.form_data.month, year: d.form_data.year })}
                                  >
                                    Run Report
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

        </div>

      </div>

      {activeReport && mockReportData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-950 rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className={`${brandObj.bgHeader} text-white p-5 flex items-center justify-between shrink-0`}>
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider opacity-85">RGA Trust Analytics</span>
                <h2 className="text-lg font-bold flex items-center gap-2 mt-0.5">
                  <FileText className="w-5 h-5 text-teal-300" />
                  {mockReportData.type === "trust" ? "Trust Receipt Ledger" : mockReportData.type === "reconciliation" ? "Reconciliation Report" : "Banking/Transfer Activity Report"}
                </h2>
                <p className="text-xs opacity-90 mt-1">
                  Period: {mockReportData.month} {mockReportData.year} • Account: {mockReportData.accountNo}
                </p>
              </div>
              <button 
                onClick={() => setActiveReport(null)}
                className="text-white hover:opacity-85 text-xs font-semibold border border-white/20 px-3 py-1.5 rounded bg-white/10"
              >
                Close
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto space-y-6 text-xs text-slate-800 dark:text-slate-200">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-slate-150 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl">
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Net Activity</div>
                  <div className={`text-lg font-bold mt-1 ${mockReportData.totalAmount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {mockReportData.totalAmount >= 0 ? "+" : ""}{formatCurrency(mockReportData.totalAmount)}
                  </div>
                </div>
                <div className="border border-slate-150 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4 rounded-xl">
                  <div className="text-[10px] text-slate-400 font-bold uppercase">Reconciliation Status</div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="font-bold text-emerald-600 uppercase tracking-wider text-[10px]">{mockReportData.status}</span>
                  </div>
                </div>
              </div>

              {/* Transactions Table */}
              <div className="space-y-2">
                <h3 className="font-bold uppercase tracking-wider text-[10px] text-slate-450 dark:text-slate-500 border-b pb-1">
                  Activity Line Items
                </h3>
                <div className="border border-slate-150 dark:border-slate-800 rounded-lg overflow-hidden">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-900 text-slate-500 font-bold border-b border-slate-150 dark:border-slate-800 text-[10px]">
                        <th className="p-2.5">Date</th>
                        <th className="p-2.5">Ref No</th>
                        <th className="p-2.5">Description</th>
                        <th className="p-2.5 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-150 dark:divide-slate-800">
                      {mockReportData.transactions.map((tx, idx) => (
                        <tr key={`tx-${idx}`} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/30">
                          <td className="p-2.5 text-slate-500">{tx.date}</td>
                          <td className="p-2.5 font-mono">{tx.ref}</td>
                          <td className="p-2.5 font-medium">{tx.description}</td>
                          <td className={`p-2.5 text-right font-semibold ${tx.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                            {tx.amount >= 0 ? "+" : ""}{formatCurrency(tx.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 dark:bg-slate-900 px-6 py-4 flex justify-between items-center shrink-0 border-t border-slate-150 dark:border-slate-800">
              <span className="text-[10px] text-slate-400">Exported from RGA Trust Compliance Portal</span>
              <Button 
                onClick={() => window.print()}
                className="bg-slate-900 hover:bg-slate-800 text-white text-[11px] font-bold uppercase tracking-wider gap-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Export PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-950 rounded-2xl max-w-4xl w-full shadow-2xl border border-slate-150 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh] animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-slate-900 text-white p-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-400 animate-pulse" />
                <span className="text-sm font-bold">Document Preview</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white/10 text-white border-white/25 hover:bg-white/20 h-8 text-xs font-semibold"
                  onClick={() => handleDownloadFile(previewUrl, "document")}
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Download
                </Button>
                <Button 
                  onClick={() => setPreviewUrl(null)}
                  className="bg-teal-650 hover:bg-teal-700 text-white h-8 text-xs font-bold px-3 py-1.5 rounded-lg"
                >
                  Close
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden min-h-[500px] bg-slate-100 dark:bg-slate-900 flex flex-col justify-stretch">
              {previewUrl.toLowerCase().includes(".pdf") || previewUrl.startsWith("data:application/pdf") ? (
                <iframe 
                  src={previewUrl} 
                  className="w-full h-full min-h-[650px] border-none"
                  title="PDF Preview"
                />
              ) : (
                <div className="flex-1 overflow-auto flex items-center justify-center p-6">
                  <img 
                    src={previewUrl} 
                    className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-lg border border-slate-200" 
                    alt="Document Preview"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
